import { makeAutoObservable, runInAction } from "mobx";
import { AxiosError } from "axios";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type { ITeamsAppPackage, TTeamsConnection } from "@/types/msteams";

/** Which round trip is in flight, so the card can show progress for the one control
 *  that started it rather than disabling all of them.
 *
 *  A single value rather than a set of booleans because the endpoints are all on one
 *  singular resource: two of these cannot sensibly overlap, and a `Record<string,
 *  boolean>` would let the UI claim they do. */
export type TTeamsPendingAction =
  | "signin"
  | "enable"
  | "disable"
  | "disconnect"
  | "test_card"
  | "app_package"
  | null;

/** The machine-readable half of a failed Teams call, so the card can say the one
 *  thing the admin can act on instead of a generic failure.
 *
 *  Three codes, and each has a different recourse: `signin_not_configured` and
 *  `bot_not_configured` are 503s meaning this *environment* is not wired up (contact
 *  support — nothing the workspace can fix), and `no_linked_chat` is a 409 meaning
 *  install the app in Teams first. Anything else stays `null` rather than being
 *  guessed at: an unrecognised failure has to reach the user as one. */
export type TTeamsErrorCode = "signin_not_configured" | "bot_not_configured" | "no_linked_chat";

const KNOWN_CODES: TTeamsErrorCode[] = [
  "signin_not_configured",
  "bot_not_configured",
  "no_linked_chat",
];

/** The workspace's Microsoft Teams connection (plan §5a, NG-1096).
 *
 *  **Singular, unlike its LMS neighbour.** A workspace consents to one Entra tenant,
 *  so this holds one row (or none) rather than a list, and no action takes an id.
 *
 *  **Optimistic where the outcome is knowable, and not where it isn't** — the design
 *  rule's documented exception, applied per verb rather than per store:
 *
 *  * `disable` applies immediately and reverts on failure — `disabled` is the only
 *    state it can land in, so it is fully derivable client-side.
 *  * `enable` waits, because the state it lands in is **not** derivable here: the
 *    server returns `enabled` or `pending_tenant_approval` depending on whether an
 *    install has ever been seen, a count the client has no way to know. Guessing it
 *    would show "Enabled" to a workspace whose Teams admin has never approved the
 *    package — the one thing FR3's states exist to distinguish.
 *  * `testCard` waits because only the Bot Connector can say whether the card landed.
 *  * `disconnect` waits because it is destructive: the consent row is soft-deleted and
 *    every link becomes `uninstalled`, which a re-consent cannot undo.
 */
export class WorkspaceTeamsConnectionStore {
  /** The connection, or `null` before the first read resolves. A workspace with no
   *  connection is `state: "not_enabled"` — a real row-shaped answer, not `null`. */
  connection: TTeamsConnection | null = null;
  isLoading = false;
  pendingAction: TTeamsPendingAction = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /** The state to render. Understates health rather than overstating it: an absent
   *  read must never look like a live channel. */
  get state(): TTeamsConnection["state"] {
    return this.connection?.state ?? "not_enabled";
  }

  async fetch(slug: string): Promise<TTeamsConnection | null> {
    this.isLoading = true;
    try {
      const connection = await this.rootStore.msTeamsConnectionService.getConnection(slug);
      runInAction(() => {
        this.connection = connection;
      });
      return connection;
    } catch (error) {
      // Warn, not error: the card renders its not-enabled state and the page is still
      // usable, so this is a degraded read rather than a broken user action.
      console.warn("[msteams] failed to read the connection", { slug, error });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /** Mint the Microsoft authorization URL. The caller navigates to it — the store does
   *  not touch `window`, so it stays testable and the redirect is visible at the call
   *  site rather than buried in a mutation.
   *
   *  `returnPath` is where the callback comes back to; pass the Integration Library
   *  route so the admin lands on the card that sent them. */
  async startSignIn(
    slug: string,
    returnPath: string,
    source: AnalyticsSource = "page",
  ): Promise<string> {
    this.pendingAction = "signin";
    try {
      const { authorization_url } = await this.rootStore.msTeamsConnectionService.startSignIn(
        slug,
        returnPath,
      );
      // The connect itself lands on the callback, so `useTeamsSignInRedirect` reports it.
      track("teams_connection_signin_started", { source });
      return authorization_url;
    } catch (error) {
      console.error("[msteams] failed to start the Microsoft sign-in", {
        slug,
        returnPath,
        code: teamsErrorCode(error),
        error,
      });
      throw error;
    } finally {
      runInAction(() => {
        this.pendingAction = null;
      });
    }
  }

  /** Un-pause the channel. Waits for the server — see the class docstring for why the
   *  resulting state is not derivable here. */
  async enable(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    this.pendingAction = "enable";
    try {
      const connection = await this.rootStore.msTeamsConnectionService.enable(slug);
      runInAction(() => {
        this.connection = connection;
      });
      track("teams_connection_enabled", { source });
    } catch (error) {
      console.error("[msteams] failed to enable the connection", { slug, error });
      throw error;
    } finally {
      runInAction(() => {
        this.pendingAction = null;
      });
    }
  }

  /** Stop every delivery in the workspace. Applied immediately: `disabled` is the only
   *  state this can land in, and the links are kept either way. `pendingAction` stays
   *  `"disable"` until the server has it (NG-2290): the badge flips at once, but an
   *  Enable sent while the Disable is in flight would race it, and whichever landed
   *  last would win. The Enable button waits on it, and its re-enabling is the signal
   *  that the Disable saved. */
  async disable(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    const before = this.connection;
    runInAction(() => {
      if (this.connection) {
        this.connection = { ...this.connection, is_enabled: false, state: "disabled" };
      }
      this.pendingAction = "disable";
    });
    try {
      const connection = await this.rootStore.msTeamsConnectionService.disable(slug);
      runInAction(() => {
        this.connection = connection;
      });
      track("teams_connection_disabled", { source });
    } catch (error) {
      console.error("[msteams] failed to disable the connection", { slug, error });
      runInAction(() => {
        this.connection = before;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.pendingAction = null;
      });
    }
  }

  /** End the consent. Destructive, so it waits; the server answers with the
   *  `not_enabled` shape, which is what the card then renders. */
  async disconnect(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    this.pendingAction = "disconnect";
    try {
      const connection = await this.rootStore.msTeamsConnectionService.disconnect(slug);
      runInAction(() => {
        this.connection = connection;
      });
      track("teams_connection_disconnected", { source });
    } catch (error) {
      console.error("[msteams] failed to disconnect the connection", { slug, error });
      throw error;
    } finally {
      runInAction(() => {
        this.pendingAction = null;
      });
    }
  }

  /** Send a card to the caller's own chat and store the outcome.
   *
   *  Resolves for a *failed* delivery too — a vendor failure is a 200 carrying
   *  `last_test_status: false`, because a failed test is a fact to show beside the
   *  connection rather than a request that went wrong. The caller reads the result out
   *  of the returned row. A 409 (`no_linked_chat`) is the one real rejection. */
  async testCard(slug: string, source: AnalyticsSource = "page"): Promise<TTeamsConnection> {
    this.pendingAction = "test_card";
    try {
      const connection = await this.rootStore.msTeamsConnectionService.sendTestCard(slug);
      runInAction(() => {
        this.connection = connection;
      });
      // A vendor failure resolves too, so the outcome is a prop rather than a missing event.
      track("teams_connection_tested", { success: connection.last_test_status, source });
      return connection;
    } catch (error) {
      console.error("[msteams] failed to send the test card", {
        slug,
        code: teamsErrorCode(error),
        error,
      });
      throw error;
    } finally {
      runInAction(() => {
        this.pendingAction = null;
      });
    }
  }

  /** Fetch the app-package zip. Returns the bytes and the server's filename; the caller
   *  hands them to the browser, so nothing here touches the DOM. */
  async downloadPackage(slug: string, source: AnalyticsSource = "page"): Promise<ITeamsAppPackage> {
    this.pendingAction = "app_package";
    try {
      const appPackage = await this.rootStore.msTeamsConnectionService.downloadAppPackage(slug);
      track("teams_app_package_downloaded", { source });
      return appPackage;
    } catch (error) {
      console.error("[msteams] failed to download the app package", {
        slug,
        code: teamsErrorCode(error),
        error,
      });
      throw error;
    } finally {
      runInAction(() => {
        this.pendingAction = null;
      });
    }
  }

  reset(): void {
    this.connection = null;
    this.isLoading = false;
    this.pendingAction = null;
  }
}

/** The `code` a Teams endpoint put in its error body, or `null`.
 *
 *  Falls back to `null` rather than inventing one, the way `toConnectError` does for
 *  the LMS store: an unrecognised failure must reach the user as an unrecognised
 *  failure, not as a confident wrong diagnosis. */
export function teamsErrorCode(error: unknown): TTeamsErrorCode | null {
  const data = (error as AxiosError<{ code?: string }>)?.response?.data;
  return KNOWN_CODES.find((known) => known === data?.code) ?? null;
}

/** Whether a failure was the 409 that means "install the Cognota app in Teams first".
 *
 *  Checks the status as well as the code, because that is the pair the endpoint
 *  promises and a 409 from anywhere else on this resource does not exist. */
export function isNoLinkedChatError(error: unknown): boolean {
  const response = (error as AxiosError<{ code?: string }>)?.response;
  return response?.status === 409 && response?.data?.code === "no_linked_chat";
}
